import { Address, Country, CurrencyCode } from '../fragments';

export type MerchantBusinessProfile = {
  timeZone: string;
  businessName: string;
  businessType: MerchantBusinessType;
  companyType?: MerchantCompanyType;
  website: string;
  supportSite: string;
  industry: MerchantIndustry;
  tradingCountry?: Country;
  taxCountry: Country;
  taxId?: string;
  contactPerson: {
    title: string;
    givenName: string;
    familyName: string;
  };
  contactEmailAddress: string;
  supportEmailAddress?: string;
  phoneNumber: string;
  address: Address;
  brandName: string;
  cardStatementName: string;
  smsSenderName: string;
  verificationDocs: {
    companyFrontFileId?: string;
    companyBackFileId?: string;
    companyTaxIdFileId?: string;
    partnershipAgreementFileId?: string;
    trustDeedFileId?: string;
    trustDeedAmendmentFileIds?: string[];
  };
};

type MerchantBranding = {
  iconUri?: string;
  logoUri?: string;
  logoAltText?: string;
  primaryColor?: string;
};

export type CustomField = {
  key: string;
  label: string;
  required: boolean;
  visibleForCustomer: boolean;
};

export type BillingDetails = {
  billingNotificationEmails?: Array<string>;
  billingNotificationsEnabled: boolean;
  billingAddress?: Address;
  billingPaymentSourceId?: string;
};

export type PayoutDetails = {
  payoutSettings: {
    notificationEmails: Array<string>;
    notificationsEnabled: boolean;
  };
  payoutSchedule: 'daily' | 'weekly' | 'monthly';
  payoutDelayDays: number;
  payoutExpediteNonBusinessDays: boolean;
  automatedPayoutsEnabled: boolean;
  payoutStatus: 'Enabled' | 'Disabled';
};

export type BaseMerchant = {
  merchantId: string;
  billingDetails: BillingDetails;
  businessProfile: MerchantBusinessProfile;
  payoutDetails: PayoutDetails;
  status: 'Pending' | 'Active' | 'Suspended';
  readableIdentifier: string;
  payPlanSettings: {
    maxPayPlanAmount: number;
    minPayPlanAmount: number;
  };
  bankAccounts: Array<BankAccount>;
};

export type StandardMerchant = BaseMerchant & {
  type: 'StandardMerchant';
  branding: MerchantBranding;
  orderCustomFields?: CustomField[];
  verificationResult?: {
    status: 'Pending' | 'Verified';
    errorCodes: Array<VerificationResultsErrorCodes>;
    details: Array<string>;
  };
};

export type StandardMerchantResponse = {
  StandardMerchant: BaseMerchant & {
    branding: MerchantBranding;
    orderCustomFields?: CustomField[];
  };
};

export type ServiceProviderMerchant = BaseMerchant & {
  type: 'ServiceProviderMerchant';
};

export type ServiceProviderMerchantResponse = {
  ServiceProviderMerchant: BaseMerchant;
};

export type MerchantResponse = StandardMerchantResponse | ServiceProviderMerchantResponse;

export type AnyMerchant = StandardMerchant | ServiceProviderMerchant;

export const handleAnyMerchant = (merchantResponse: MerchantResponse): AnyMerchant =>
  'StandardMerchant' in merchantResponse
    ? { ...merchantResponse.StandardMerchant, type: 'StandardMerchant' }
    : { ...merchantResponse.ServiceProviderMerchant, type: 'ServiceProviderMerchant' };

type SupportedCustomerType = 'consumer' | 'organisation';

type VelocityConfigLimit = {
  limitPerSecond: number;
  limitPerMinute: number;
  limitPerHour: number;
};

export type MerchantVelocityConfig = {
  limits_order_marketplaceId: VelocityConfigLimit;
  limits_order_internalOrderId: VelocityConfigLimit;
  limits_order_paymentAmount: VelocityConfigLimit;
  limits_order_billing_name: VelocityConfigLimit;
  limits_order_billing_address: VelocityConfigLimit;
  limits_order_billing_items: VelocityConfigLimit;
  limits_order_ipAddress: VelocityConfigLimit;
  limits_order_checkoutCorrelationId: VelocityConfigLimit;
  limits_payto_resolveAlias?: VelocityConfigLimit;
  limits_payto_sendAgreement?: VelocityConfigLimit;
  limits_payto_getAgreementStatus?: VelocityConfigLimit;
};

export type MerchantGeneralSettingsResponse = {
  showSystemLogoInTermsPage: boolean;
  showCustomerSavedPaymentMethodsInCheckout: boolean;
  enableHideFullPay: boolean;
  showReferenceOrderIdToCustomer: boolean;
  enableVirtualTerminalPayPlans?: boolean;
  settlementType: 'Net' | 'Gross';
  payPlanVariants?: Array<string>;
  supportedCustomerTypes: Array<SupportedCustomerType>;
  payPlanMerchantTransferDuration?: string;
  sourceVerificationEnabled: boolean;
  velocityConfig?: MerchantVelocityConfig;
  kycSettings: {
    kycLimitCustomerLinks: number;
    idDocRequirement: 'licenceorpassport' | 'licenceandpassport';
  };
  eligibilitySettings?: {
    riskRating: object;
    classification: 'lowrisk' | 'normal' | 'highrisk';
    overrideMinLoanPercent: number;
    allowCounterOffer: boolean;
  };
  emailCustomerReceipt?: boolean;
  emailCustomerFailure?: boolean;
  inhibitGooglePay?: boolean;
  payInFullMinCheckoutLimit?: number;
  payInFullMaxCheckoutLimit?: number;
  disbursementSourceAccountId?: string;
  disbursementPriority?: number;
  createdAt: string;
  updatedAt: string;
};

export type PutMerchantGeneralSettingsRequest = {
  showSystemLogoInTermsPage: boolean;
  showCustomerSavedPaymentMethodsInCheckout: boolean;
  enableHideFullPay: boolean;
  showReferenceOrderIdToCustomer: boolean;
  enableVirtualTerminalPayPlans?: boolean;
  settlementType: 'Net' | 'Gross';
  payPlanVariants?: Array<string>;
  supportedCustomerTypes: Array<SupportedCustomerType>;
  payPlanMerchantTransferDuration?: string;
  sourceVerificationEnabled: boolean;
  velocityConfig?: MerchantVelocityConfig;
  kycSettings: {
    kycLimitCustomerLinks: number;
    idDocRequirement: 'licenceorpassport' | 'licenceandpassport';
  };
  eligibilitySettings?: {
    riskRating: object;
    classification: 'lowrisk' | 'normal' | 'highrisk';
    overrideMinLoanPercent: number;
    allowCounterOffer: boolean;
  };
  emailCustomerReceipt?: boolean;
  emailCustomerFailure?: boolean;
  inhibitGooglePay?: boolean | null;
  payInFullMinCheckoutLimit?: number;
  payInFullMaxCheckoutLimit?: number;
  disbursementSourceAccountId?: string;
  disbursementPriority?: number;
  createdAt: string;
  updatedAt: string;
};

export type PayoutStatusUpdateRequest = {
  payoutStatus: 'Enabled' | 'Disabled';
};

export type MerchantStatusResponse = {
  isSuspended: boolean;
  isCheckoutEnabled: boolean;
  isActivated: boolean;
  payoutStatus: 'Enabled' | 'Disabled';
  isVerified: boolean;
  state:
    | 'Unknown'
    | 'Onboarding'
    | 'OnboardingReview'
    | 'Active'
    | 'ManuallyActive'
    | 'CheckoutDisabled'
    | 'Suspended'
    | 'Closed';
};

export type VerificationResultsErrorCodes =
  | 'InvalidAddress'
  | 'DocumentAddressMismatch'
  | 'DocumentAddressMissing'
  | 'DocumentCountryNotSupported'
  | 'DocumentDobMismatch'
  | 'DocumentDuplicateType'
  | 'DocumentExpired'
  | 'DocumentFailedCopy'
  | 'DocumentFailedGreyscale'
  | 'DocumentFailedOther'
  | 'DocumentFraudulent'
  | 'DocumentIdNumberMismatch'
  | 'DocumentIdNumberMissing'
  | 'DocumentIncomplete'
  | 'DocumentInvalid'
  | 'DocumentIssueOrExpiryDateMissing'
  | 'DocumentMissingBack'
  | 'DocumentMissingFront'
  | 'DocumentSameFrontBack'
  | 'DocumentNameMismatch'
  | 'DocumentNameMissing'
  | 'DocumentNationalityMismatch'
  | 'DocumentNotReadable'
  | 'DocumentNotSigned'
  | 'DocumentNotUploaded'
  | 'DocumentPhotoMismatch'
  | 'DocumentTooLarge'
  | 'DocumentTypeNotSupported'
  | 'FailedAddressMatch'
  | 'FailedBusinessIecNumber'
  | 'FailedDocumentMatch'
  | 'FailedIdNumberMatch'
  | 'FailedKeyedIdentity'
  | 'FailedKeyedMatch'
  | 'FailedNameMatch'
  | 'FailedOther'
  | 'FailedTaxIdMatch'
  | 'FailedTaxIdNotIssued'
  | 'InvalidTaxId'
  | 'RequiresAdditionalMemorandumOfAssociations'
  | 'MissingExecutives'
  | 'MissingOwners'
  | 'MissingDirectors'
  | 'MissingOwnerOrExecutive'
  | 'MissingIndividual'
  | 'PersonInformationMissing'
  | 'MissingGatewayPersonAccount'
  | 'InvalidPersonAge'
  | 'MissingBankAccount'
  | 'MerchantGatewayAccountMissing'
  | 'InvalidBusinessName'
  | 'CompanyTypeMissing'
  | 'PartnershipAgreementDocMissing'
  | 'TrustDeedDocMissing'
  | 'InvalidPostalCode'
  | 'InvalidState'
  | 'InvalidCity'
  | 'InvalidPhoneNum'
  | 'InvalidTimeZone'
  | 'InvalidWebsite'
  | 'InvalidSupportSite'
  | 'InvalidCardStatementName'
  | 'InvalidSmsSenderName'
  | 'InvalidBrandName'
  | 'InvalidContactEmail'
  | 'InvalidSupportEmail'
  | 'InvalidSlug'
  | 'DocumentBackNotNeeded'
  | 'DriverLicenceMissing'
  | 'PassportMissing'
  | 'IdMissing'
  | 'MultipleIdsFound'
  | 'UnsupportedIdType'
  | 'DriverLicenceStateMissing'
  | 'DriverLicenceDocumentNumberMissing'
  | 'DriverLicenceVersionNumberMissing'
  | 'InvalidNzDriverLicenceVersion'
  | 'DriverLicenceStateNotNeeded'
  | 'PassportExpired'
  | 'InvalidBusinessContactFirstName'
  | 'InvalidBusinessContactLastName'
  | 'InvalidBusinessCountry'
  | 'InvalidBusinessAddress'
  | 'InvalidPersonAddress'
  | 'InvalidFirstName'
  | 'InvalidLastName'
  | 'InvalidTradeType'
  | 'InvalidTradeLicence'
  | 'MerchantIsNotActive'
  | 'MerchantAlreadyVerified'
  | 'MerchantStateInvalid'
  | 'IndustryMissing'
  | 'UnknownError';

export type MerchantBusinessType = 'company' | 'individual' | 'soletrader' | 'partnership' | 'trust';

export type MerchantCompanyType = 'Private' | 'Public';

export type MerchantIndustry =
  | 'AcRefrigerationRepair'
  | 'AccountingBookkeepingServices'
  | 'AdvertisingServices'
  | 'AgriculturalCooperative'
  | 'AirlinesAirCarriers'
  | 'AirportsFlyingFields'
  | 'AmbulanceServices'
  | 'AmusementParksCarnivals'
  | 'AntiqueReproductions'
  | 'AntiqueShops'
  | 'Aquariums'
  | 'ArchitecturalSurveyingServices'
  | 'ArtDealersAndGalleries'
  | 'ArtistsSupplyAndCraftShops'
  | 'AutoBodyRepairShops'
  | 'AutoPaintShops'
  | 'AutoServiceShops'
  | 'AutoAndHomeSupplyStores'
  | 'AutomatedCashDisburse'
  | 'AutomatedFuelDispensers'
  | 'AutomobileAssociations'
  | 'AutomotivePartsAndAccessoriesStores'
  | 'AutomotiveTireStores'
  | 'BailAndBondPayments'
  | 'Bakeries'
  | 'BandsOrchestras'
  | 'BarberAndBeautyShops'
  | 'BettingCasinoGambling'
  | 'BicycleShops'
  | 'BilliardPoolEstablishments'
  | 'BoatDealers'
  | 'BoatRentalsAndLeases'
  | 'BookStores'
  | 'BooksPeriodicalsAndNewspapers'
  | 'BowlingAlleys'
  | 'BusLines'
  | 'BusinessSecretarialSchools'
  | 'BuyingShoppingServices'
  | 'CableSatelliteAndOtherPayTelevisionAndRadio'
  | 'CameraAndPhotographicSupplyStores'
  | 'CandyNutAndConfectioneryStores'
  | 'CarRentalAgencies'
  | 'CarWashes'
  | 'CarAndTruckDealersNewUsed'
  | 'CarAndTruckDealersUsedOnly'
  | 'CarpentryServices'
  | 'CarpetUpholsteryCleaning'
  | 'Caterers'
  | 'CharitableAndSocialServiceOrganizationsFundraising'
  | 'ChemicalsAndAlliedProducts'
  | 'ChildCareServices'
  | 'ChildrensAndInfantsWearStores'
  | 'ChiropodistsPodiatrists'
  | 'Chiropractors'
  | 'CigarStoresAndStands'
  | 'CivicSocialFraternalAssociations'
  | 'CleaningAndMaintenance'
  | 'ClothingRental'
  | 'CollegesUniversities'
  | 'CommercialEquipment'
  | 'CommercialFootwear'
  | 'CommercialPhotographyArtAndGraphics'
  | 'CommuterTransportAndFerries'
  | 'ComputerNetworkServices'
  | 'ComputerProgramming'
  | 'ComputerRepair'
  | 'ComputerSoftwareStores'
  | 'ComputersPeripheralsAndSoftware'
  | 'ConcreteWorkServices'
  | 'ConstructionMaterials'
  | 'ConsultingPublicRelations'
  | 'CorrespondenceSchools'
  | 'CosmeticStores'
  | 'CounselingServices'
  | 'CountryClubs'
  | 'CourierServices'
  | 'CourtCosts'
  | 'CreditReportingAgencies'
  | 'CruiseLines'
  | 'DairyProductsStores'
  | 'DanceHallStudiosSchools'
  | 'DatingEscortServices'
  | 'DentistsOrthodontists'
  | 'DepartmentStores'
  | 'DetectiveAgencies'
  | 'DigitalGoodsMedia'
  | 'DigitalGoodsApplications'
  | 'DigitalGoodsGames'
  | 'DigitalGoodsLargeVolume'
  | 'DirectMarketingCatalogMerchant'
  | 'DirectMarketingCombinationCatalogAndRetailMerchant'
  | 'DirectMarketingInboundTelemarketing'
  | 'DirectMarketingInsuranceServices'
  | 'DirectMarketingOther'
  | 'DirectMarketingOutboundTelemarketing'
  | 'DirectMarketingSubscription'
  | 'DirectMarketingTravel'
  | 'DiscountStores'
  | 'Doctors'
  | 'DoorToDoorSales'
  | 'DraperyWindowCoveringAndUpholsteryStores'
  | 'DrinkingPlaces'
  | 'DrugStoresAndPharmacies'
  | 'DrugsDrugProprietariesAndDruggistSundries'
  | 'DryCleaners'
  | 'DurableGoods'
  | 'DutyFreeStores'
  | 'EatingPlacesRestaurants'
  | 'EducationalServices'
  | 'ElectricRazorStores'
  | 'ElectricalPartsAndEquipment'
  | 'ElectricalServices'
  | 'ElectronicsRepairShops'
  | 'ElectronicsStores'
  | 'ElementarySecondarySchools'
  | 'EmploymentTempAgencies'
  | 'EquipmentRental'
  | 'ExterminatingServices'
  | 'FamilyClothingStores'
  | 'FastFoodRestaurants'
  | 'FinancialInstitutions'
  | 'FinesGovernmentAdministrativeEntities'
  | 'FireplaceFireplaceScreensAndAccessoriesStores'
  | 'FloorCoveringStores'
  | 'Florists'
  | 'FloristsSuppliesNurseryStockAndFlowers'
  | 'FreezerAndLockerMeatProvisioners'
  | 'FuelDealersNonAutomotive'
  | 'FuneralServicesCrematories'
  | 'FurnitureRepairRefinishing'
  | 'FurnitureHomeFurnishingsAndEquipmentStoresExceptAppliances'
  | 'FurriersAndFurShops'
  | 'GeneralServices'
  | 'GiftCardNoveltyAndSouvenirShops'
  | 'GlassPaintAndWallpaperStores'
  | 'GlasswareCrystalStores'
  | 'GolfCoursesPublic'
  | 'GovernmentServices'
  | 'GroceryStoresSupermarkets'
  | 'HardwareStores'
  | 'HardwareEquipmentAndSupplies'
  | 'HealthAndBeautySpas'
  | 'HearingAidsSalesAndSupplies'
  | 'HeatingPlumbingAC'
  | 'HobbyToyAndGameShops'
  | 'HomeSupplyWarehouseStores'
  | 'Hospitals'
  | 'HotelsMotelsAndResorts'
  | 'HouseholdApplianceStores'
  | 'IndustrialSupplies'
  | 'InformationRetrievalServices'
  | 'InsuranceDefault'
  | 'InsuranceUnderwritingPremiums'
  | 'IntraCompanyPurchases'
  | 'JewelryStoresWatchesClocksAndSilverwareStores'
  | 'LandscapingServices'
  | 'Laundries'
  | 'LaundryCleaningServices'
  | 'LegalServicesAttorneys'
  | 'LuggageAndLeatherGoodsStores'
  | 'LumberBuildingMaterialsStores'
  | 'ManualCashDisburse'
  | 'MarinasServiceAndSupplies'
  | 'MasonryStoneworkAndPlaster'
  | 'MassageParlors'
  | 'MedicalServices'
  | 'MedicalAndDentalLabs'
  | 'MedicalDentalOphthalmicAndHospitalEquipmentAndSupplies'
  | 'MembershipOrganizations'
  | 'MensAndBoysClothingAndAccessoriesStores'
  | 'MensWomensClothingStores'
  | 'MetalServiceCenters'
  | 'MiscellaneousApparelAndAccessoryShops'
  | 'MiscellaneousAutoDealers'
  | 'MiscellaneousBusinessServices'
  | 'MiscellaneousFoodStores'
  | 'MiscellaneousGeneralMerchandise'
  | 'MiscellaneousGeneralServices'
  | 'MiscellaneousHomeFurnishingSpecialtyStores'
  | 'MiscellaneousPublishingAndPrinting'
  | 'MiscellaneousRecreationServices'
  | 'MiscellaneousRepairShops'
  | 'MiscellaneousSpecialtyRetail'
  | 'MobileHomeDealers'
  | 'MotionPictureTheaters'
  | 'MotorFreightCarriersAndTrucking'
  | 'MotorHomesDealers'
  | 'MotorVehicleSuppliesAndNewParts'
  | 'MotorcycleShopsAndDealers'
  | 'MotorcycleShopsDealers'
  | 'MusicStoresMusicalInstrumentsPianosAndSheetMusic'
  | 'NewsDealersAndNewsstands'
  | 'NonFiMoneyOrders'
  | 'NonFiStoredValueCardPurchaseLoad'
  | 'NondurableGoods'
  | 'NurseriesLawnAndGardenSupplyStores'
  | 'NursingPersonalCare'
  | 'OfficeAndCommercialFurniture'
  | 'OpticiansEyeglasses'
  | 'OptometristsOphthalmologist'
  | 'OrthopedicGoodsProstheticDevices'
  | 'Osteopaths'
  | 'PackageStoresBeerWineAndLiquor'
  | 'PaintsVarnishesAndSupplies'
  | 'ParkingLotsGarages'
  | 'PassengerRailways'
  | 'PawnShops'
  | 'PetShopsPetFoodAndSupplies'
  | 'PetroleumAndPetroleumProducts'
  | 'PhotoDeveloping'
  | 'PhotographicStudios'
  | 'PhotographicPhotocopyMicrofilmEquipmentAndSupplies'
  | 'PictureVideoProduction'
  | 'PieceGoodsNotionsAndOtherDryGoods'
  | 'PlumbingHeatingEquipmentAndSupplies'
  | 'PoliticalOrganizations'
  | 'PostalServicesGovernmentOnly'
  | 'PreciousStonesAndMetalsWatchesAndJewelry'
  | 'ProfessionalServices'
  | 'PublicWarehousingAndStorage'
  | 'QuickCopyReproAndBlueprint'
  | 'Railroads'
  | 'RealEstateAgentsAndManagersRentals'
  | 'RecordStores'
  | 'RecreationalVehicleRentals'
  | 'ReligiousGoodsStores'
  | 'ReligiousOrganizations'
  | 'RoofingSidingSheetMetal'
  | 'SecretarialSupportServices'
  | 'SecurityBrokersDealers'
  | 'ServiceStations'
  | 'SewingNeedleworkFabricAndPieceGoodsStores'
  | 'ShoeRepairHatCleaning'
  | 'ShoeStores'
  | 'SmallApplianceRepair'
  | 'SnowmobileDealers'
  | 'SpecialTradeServices'
  | 'SpecialtyCleaning'
  | 'SportingGoodsStores'
  | 'SportingRecreationCamps'
  | 'SportsClubsFields'
  | 'SportsAndRidingApparelStores'
  | 'StampAndCoinStores'
  | 'StationaryOfficeSuppliesPrintingAndWritingPaper'
  | 'StationeryStoresOfficeAndSchoolSupplyStores'
  | 'SwimmingPoolsSales'
  | 'TUiTravelGermany'
  | 'TailorsAlterations'
  | 'TaxPaymentsGovernmentAgencies'
  | 'TaxPreparationServices'
  | 'TaxicabsLimousines'
  | 'TelecommunicationEquipmentAndTelephoneSales'
  | 'TelecommunicationServices'
  | 'TelegraphServices'
  | 'TentAndAwningShops'
  | 'TestingLaboratories'
  | 'TheatricalTicketAgencies'
  | 'Timeshares'
  | 'TireRetreadingAndRepair'
  | 'TollsBridgeFees'
  | 'TouristAttractionsAndExhibits'
  | 'TowingServices'
  | 'TrailerParksCampgrounds'
  | 'TransportationServices'
  | 'TravelAgenciesTourOperators'
  | 'TruckStopIteration'
  | 'TruckUtilityTrailerRentals'
  | 'TypesettingPlateMakingAndRelatedServices'
  | 'TypewriterStores'
  | 'USFederalGovernmentAgenciesOrDepartments'
  | 'UniformsCommercialClothing'
  | 'UsedMerchandiseAndSecondhandStores'
  | 'Utilities'
  | 'VarietyStores'
  | 'VeterinaryServices'
  | 'VideoAmusementGameSupplies'
  | 'VideoGameArcades'
  | 'VideoTapeRentalStores'
  | 'VocationalTradeSchools'
  | 'WatchJewelryRepair'
  | 'WeldingRepair'
  | 'WholesaleClubs'
  | 'WigAndToupeeStores'
  | 'WiresMoneyOrders'
  | 'WomensAccessoryAndSpecialtyShops'
  | 'WomensReadyToWearStores'
  | 'WreckingAndSalvageYards';

export type TradingCountry = 'AU' | 'NZ' | 'US' | 'SG' | 'ZA' | 'HK';
export type TaxCountry = 'AU' | 'NZ' | 'US' | 'SG' | 'ZA' | 'HK';

export type MerchantPerson = {
  personId: string;
  profile: {
    title: string;
    roles: Array<'Executive' | 'Owner' | 'Director'>;
    firstName: string;
    middleName?: string;
    lastName: string;
    dob: string;
    address: Address;
    email: string;
    phone: string;
    passport?: {
      country: Country;
      passportNumber: string;
      expiryDate: string;
    };
    driverLicence?: {
      country: Country;
      driverLicenceNumber: string;
      documentNumber?: string;
      versionNumber?: string;
      state?: string;
    };
    verificationDocs?: {
      idType: 'Passport' | 'DriverLicence';
      docBackFileId?: string;
      docFrontFileId: string;
    };
  };
  verificationResult: {
    status: 'Pending' | 'Verified';
    errorCodes?: Array<MerchantPersonVerificationErrorCodes>;
    details?: Array<string>;
  };
  isDeletable: boolean;
};

type MerchantPersonVerificationErrorCodes = [
  | 'InvalidAddress'
  | 'DocumentAddressMismatch'
  | 'DocumentAddressMissing'
  | 'DocumentCountryNotSupported'
  | 'DocumentDobMismatch'
  | 'DocumentDuplicateType'
  | 'DocumentExpired'
  | 'DocumentFailedCopy'
  | 'DocumentFailedGreyscale'
  | 'DocumentFailedOther'
  | 'DocumentFraudulent'
  | 'DocumentIdNumberMismatch'
  | 'DocumentIdNumberMissing'
  | 'DocumentIncomplete'
  | 'DocumentInvalid'
  | 'DocumentIssueOrExpiryDateMissing'
  | 'DocumentMissingBack'
  | 'DocumentMissingFront'
  | 'DocumentSameFrontBack'
  | 'DocumentNameMismatch'
  | 'DocumentNameMissing'
  | 'DocumentNationalityMismatch'
  | 'DocumentNotReadable'
  | 'DocumentNotSigned'
  | 'DocumentNotUploaded'
  | 'DocumentPhotoMismatch'
  | 'DocumentTooLarge'
  | 'DocumentTypeNotSupported'
  | 'FailedAddressMatch'
  | 'FailedBusinessIecNumber'
  | 'FailedDocumentMatch'
  | 'FailedIdNumberMatch'
  | 'FailedKeyedIdentity'
  | 'FailedKeyedMatch'
  | 'FailedNameMatch'
  | 'FailedOther'
  | 'FailedTaxIdMatch'
  | 'FailedTaxIdNotIssued'
  | 'InvalidTaxId'
  | 'RequiresAdditionalMemorandumOfAssociations'
  | 'MissingExecutives'
  | 'MissingOwners'
  | 'MissingDirectors'
  | 'MissingOwnerOrExecutive'
  | 'MissingIndividual'
  | 'PersonInformationMissing'
  | 'MissingGatewayPersonAccount'
  | 'InvalidPersonAge'
  | 'MissingBankAccount'
  | 'MerchantGatewayAccountMissing'
  | 'InvalidBusinessName'
  | 'CompanyTypeMissing'
  | 'PartnershipAgreementDocMissing'
  | 'TrustDeedDocMissing'
  | 'InvalidPostalCode'
  | 'InvalidState'
  | 'InvalidCity'
  | 'InvalidPhoneNum'
  | 'InvalidTimeZone'
  | 'InvalidWebsite'
  | 'InvalidSupportSite'
  | 'InvalidCardStatementName'
  | 'InvalidSmsSenderName'
  | 'InvalidBrandName'
  | 'InvalidContactEmail'
  | 'InvalidSupportEmail'
  | 'InvalidSlug'
  | 'DocumentBackNotNeeded'
  | 'DriverLicenceMissing'
  | 'PassportMissing'
  | 'IdMissing'
  | 'MultipleIdsFound'
  | 'UnsupportedIdType'
  | 'DriverLicenceStateMissing'
  | 'DriverLicenceDocumentNumberMissing'
  | 'DriverLicenceVersionNumberMissing'
  | 'InvalidNzDriverLicenceVersion'
  | 'DriverLicenceStateNotNeeded'
  | 'PassportExpired'
  | 'InvalidBusinessContactFirstName'
  | 'InvalidBusinessContactLastName'
  | 'InvalidBusinessCountry'
  | 'InvalidBusinessAddress'
  | 'InvalidPersonAddress'
  | 'InvalidFirstName'
  | 'InvalidLastName'
  | 'InvalidTradeType'
  | 'InvalidTradeLicence'
  | 'MerchantIsNotActive'
  | 'MerchantAlreadyVerified'
  | 'MerchantStateInvalid'
  | 'IndustryMissing'
  | 'UnknownError',
];

export type CreateMerchantPersonRequest = {
  title: string;
  roles: Array<'Executive' | 'Owner' | 'Director'>;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string;
  address: Address;
  email: string;
  phone: string;
  passport?: {
    country: Country;
    passportNumber: string;
    expiryDate: string;
  };
  driverLicence?: {
    country: Country;
    driverLicenceNumber: string;
    documentNumber?: string;
    versionNumber?: string;
    state?: string;
  };
  verificationDocs?: {
    idType: 'Passport' | 'DriverLicence';
    docBackFileId?: string;
    docFrontFileId: string;
  };
};

export type UpdateMerchantBusinessProfileRequest = {
  timeZone: string;
  businessName: string;
  businessType: MerchantBusinessType;
  companyType?: MerchantCompanyType;
  website: string;
  supportSite: string;
  industry: MerchantIndustry;
  taxCountry: Country;
  tradingCountry?: Country;
  taxId: string;
  contactPerson: {
    title: string;
    firstName: string;
    lastName: string;
  };
  contactEmail: string;
  supportEmail?: string;
  phoneNo: string;
  address: Address;
  brandName: string;
  cardStatementName: string;
  smsSenderName: string;
  verificationDocs: {
    companyBackFileId?: string;
    companyFrontFileId?: string;
    companyTaxIdFileId?: string;
    partnershipAgreementFileId?: string;
    trustDeedFileId?: string;
    trustDeedAmendmentFileIds?: string[];
  };
};

export type kybResponse = {
  status: 'Pending' | 'Verified';
  errorCodes: VerificationResultsErrorCodes;
  details: Array<string>;
};

export type BankAccount = {
  accountId: string;
  accountName: string;
  bankName?: string;
  identifier:
    | AuBankAccountIdentifier
    | HkBankAccountIdentifier
    | HkUsdBankAccountIdentifier
    | NzBankAccountIdentifier
    | SgBankAccountIdentifier;
  currency: CurrencyCode;
  country: string;
  isPrimaryForCurrency: boolean;
  isBlocked: boolean;
  blockReason?: string;
  referenceAccountId?: string;
};

type AuBankAccountIdentifier = {
  AuBankAccountIdentifier: {
    bsb: string;
    accountNumber: string;
  };
};

type HkBankAccountIdentifier = {
  HkBankAccountIdentifier: {
    accountNumber: string;
  };
};

type HkUsdBankAccountIdentifier = {
  HkUsdBankAccountIdentifier: {
    accountNumber: string;
  };
};

type NzBankAccountIdentifier = {
  NzBankAccountIdentifier: {
    accountNumber: string;
  };
};

type SgBankAccountIdentifier = {
  SgBankAccountIdentifier: {
    accountNumber: string;
  };
};
